/**
 * Copying from a terminal pane with the keyboard (Terminal board card 3,
 * 21.10.2026). The chords follow the terminals people already use:
 *
 *   copy        Windows/Linux: Ctrl+Shift+C, or Ctrl+C while text is selected
 *               (Windows Terminal; without a selection Ctrl+C stays ^C)
 *               macOS: ⌘C
 *   copy mode   Ctrl+Shift+X (WezTerm), ⌘⇧X on macOS
 *
 * The xterm-facing helpers take the xterm instance; the chord tests are pure.
 */
import { copyModeSelection, lastOutputRange } from './copyModeModel';

const only = (e, { ctrl = false, shift = false, meta = false }) =>
  !!e.ctrlKey === ctrl && !!e.shiftKey === shift && !!e.metaKey === meta && !e.altKey;
const keyIs = (e, letter) => String(e.key || '').toLowerCase() === letter;

/** True when `e` should copy the selection instead of reaching the shell. */
export function isCopyChord(e, isMac, hasSelection) {
  if (!e || e.type !== 'keydown' || !keyIs(e, 'c')) return false;
  if (isMac) return only(e, { meta: true }) && hasSelection;
  if (only(e, { ctrl: true, shift: true })) return true;
  return only(e, { ctrl: true }) && hasSelection;
}

/** True for the copy-mode chord. */
export function isCopyModeChord(e, isMac) {
  if (!e || e.type !== 'keydown' || !keyIs(e, 'x')) return false;
  return isMac ? only(e, { meta: true, shift: true }) : only(e, { ctrl: true, shift: true });
}

/** The xterm buffer as copyModeModel sees it. */
export function bufferView(term) {
  const b = term.buffer.active;
  return {
    lines: b.length,
    cols: term.cols,
    rows: term.rows,
    line: (y) => b.getLine(y)?.translateToString(true) ?? '',
  };
}

/** Show the copy-mode state as the xterm selection, and keep it on screen. */
export function applyCopySelection(term, state) {
  const sel = copyModeSelection(state, term.cols);
  if (sel.lines) term.selectLines(sel.lines[0], sel.lines[1]);
  else term.select(sel.col, sel.row, sel.length);
  const top = term.buffer.active.viewportY;
  if (state.y < top) term.scrollToLine(state.y);
  else if (state.y >= top + term.rows) term.scrollToLine(state.y - term.rows + 1);
}

/** Text of buffer lines [from, to]; soft-wrapped rows join without a newline. */
export function linesText(term, from, to) {
  const b = term.buffer.active;
  let out = '';
  for (let y = from; y <= to; y += 1) {
    const line = b.getLine(y);
    if (!line) continue;
    if (y > from && !line.isWrapped) out += '\n';
    out += line.translateToString(true);
  }
  return out.replace(/\s+$/, '');
}

/** The last finished command's output, or null when the shell marks no prompts. */
export function lastOutputText(term, promptLines) {
  const range = lastOutputRange(promptLines);
  return range ? linesText(term, range[0], range[1]) : null;
}

/**
 * The selected text as it should land on the clipboard: blanks at the end of
 * a row go, as Windows Terminal trims them. Line breaks stay xterm's (CRLF on
 * Windows).
 */
export const selectionText = (term) => (term?.getSelection() || '').replace(/[^\S\r\n]+$/gm, '');

/** Put `text` on the clipboard; false when there was nothing to copy or it failed. */
export async function copyToClipboard(text) {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
